import {
  Accordion as MuiAccordion,
  AccordionProps as MuiAccordionProps,
  accordionClasses,
  accordionActionsClasses,
  accordionDetailsClasses,
  accordionSummaryClasses,
  styled,
  useThemeProps,
} from '@mui/material';
import { unstable_extendSxProp as extendSxProp } from '@mui/system';
import { getToken } from '@workiva/unify-tokens';
import * as React from 'react';
import {
  accordionDetailsThemeSpacingAmount,
  accordionSummaryContentHeaderSpacingAmount,
  accordionSummaryThemeSpacingAmount,
} from 'src/components/Accordion/sharedAccordionConstants';
import { useUnifyElevationProp } from 'src/styles/elevationUtils';
import { iconSizes } from '../UnifyIcons/iconSize';

/**
 * These props are only used by our custom Unify component - they should never be passed to the MUI component.
 */
export type UnifyAccordionOwnProps = {
  /**
   * The alignment to use for the expand/collapse indicator icon.
   * @default 'left'
   */
  alignment?: 'left' | 'right';
  /**
   * When gutters are disabled, the option show or remove dividers.
   * @default false
   */
  disableDividers?: boolean;
  /**
   * If `true`, the children are indented.
   * @default: true
   */
  insetContent?: boolean;
};

export type AccordionProps = Omit<
  MuiAccordionProps,
  'expandIcon' | keyof UnsupportedProps
> &
  UnifyAccordionOwnProps &
  UnsupportedProps;

/**
 * Deprecate props that aren't supported in Unify.
 */
type UnsupportedProps = {
  /**
   * Unify system elevation level that determines shadow depth when `variant` is not set to `outlined`.
   * Accepts values between 0 and 6 inclusive.
   * @default 2
   * @deprecated Unsupported prop: This prop is not supported in Unify design guidelines and will be removed in the next major.
   * Please reach out to us in #unify-design-system-support with your designer if you have a use case for this prop.
   */
  elevation?: number;
};

/**
 *
 * Demos:
 *
 * - [Accordion](https://mui.com/material-ui/react-accordion/)
 *
 * API:
 *
 * - [Accordion API](https://mui.com/material-ui/api/accordion/)
 * - inherits [Paper API](https://mui.com/material-ui/api/paper/)
 */
export const Accordion = React.forwardRef<HTMLDivElement, AccordionProps>(
  function Accordion(inProps, forwardedRef) {
    const themeProps = useThemeProps({
      props: inProps,
      name: 'MuiAccordion',
    });
    const props = extendSxProp(themeProps);
    const {
      children,
      variant = 'elevation',
      disableGutters = true,
      disableDividers = false,
      square = true,
      alignment = 'left',
      elevation: unifyElevation = variant === 'outlined' ? 0 : 2, // eslint-disable-line deprecation/deprecation
      insetContent = true,
      ...rest
    } = props;

    const muiElevation = useUnifyElevationProp(unifyElevation);
    const ownerState = {
      alignment,
      disableDividers,
      insetContent,
    };

    return (
      <UnifyAccordion
        disableGutters={disableGutters}
        square={square}
        elevation={muiElevation}
        variant={variant}
        ownerState={ownerState}
        ref={forwardedRef}
        {...rest}
      >
        {children}
      </UnifyAccordion>
    );
  }
);

type StyledUnifyAccordionProps = MuiAccordionProps & {
  ownerState: Required<UnifyAccordionOwnProps>;
};

const UnifyAccordion = styled(MuiAccordion, {
  name: 'UnifyAccordion',
  slot: 'Root',
})<StyledUnifyAccordionProps>(function UnifyAccordion({
  theme,
  disableGutters = true,
  variant = 'elevation',
  ownerState,
}) {
  const { alignment, disableDividers, insetContent } = ownerState;
  // to disable dividers, we need disable gutters
  const shouldDisableDividers = disableGutters && disableDividers;
  const spaceFromExpandIconToSummaryEdge =
    theme.spacingScalingFactor * accordionSummaryThemeSpacingAmount;
  const spaceFromContentHeaderToExpandIcon =
    theme.spacingScalingFactor * accordionSummaryContentHeaderSpacingAmount;
  const expandIconWidth = iconSizes.medium;
  const expandIconPadding = theme.spacingScalingFactor * 1.5;
  const leftAlignmentDetailsIndentation =
    insetContent === false
      ? theme.spacing(accordionDetailsThemeSpacingAmount)
      : spaceFromExpandIconToSummaryEdge +
        spaceFromContentHeaderToExpandIcon +
        // (In right alignment, there is no icon to the left of the header)
        (alignment !== 'right' ? expandIconWidth + expandIconPadding : 0);
  const alignmentStyles = {
    ...(alignment === 'left' && {
      [`& .${accordionSummaryClasses.root}`]: {
        flexDirection: 'row-reverse',
      },
      // Left-align the details and actions with the header
      [`& .${accordionDetailsClasses.root}, & .${accordionActionsClasses.root}`]:
        {
          paddingLeft: leftAlignmentDetailsIndentation,
          paddingRight: theme.spacing(accordionDetailsThemeSpacingAmount),
        },
    }),
    ...(alignment === 'right' && {
      [`& .${accordionDetailsClasses.root}, & .${accordionActionsClasses.root}`]:
        {
          // Left-align the details and actions with the header
          paddingLeft: leftAlignmentDetailsIndentation,
          paddingRight: theme.spacing(accordionDetailsThemeSpacingAmount),
        },
    }),
  };

  return {
    //
    // Adjacent Accordion Spacing
    //
    ...(!disableGutters && {
      [`& + &, &.${accordionClasses.expanded} + &`]: {
        marginTop: theme.spacing(accordionSummaryThemeSpacingAmount),
      },
    }),
    //
    // Custom Disabled Styling
    //
    [`&.${accordionClasses.disabled}`]: {
      backgroundColor: getToken('Core/Layer/layer-default-background'),
    },
    //
    // Rounded Edges with Gutters
    //
    [`&.${accordionClasses.rounded}`]: {
      ...(!disableGutters && {
        borderRadius: (theme.vars ?? theme).shape.borderRadius,
      }),
    },
    //
    // Disable Dividers Styling
    //
    ...(variant === 'elevation' &&
      shouldDisableDividers && {
        [`&:not(:first-of-type)`]: {
          clipPath: 'inset(0px -10px -10px -10px)',
        },
      }),
    //
    // Horizontal Divider Customization
    //
    ...(variant === 'outlined' && {
      '&:before': {
        // Never display the horizontal divider when each accordion
        // is already displayed as an outlined paper element.
        display: 'none !important',
      },
      ...(shouldDisableDividers && {
        [`&:not(:last-of-type)`]: {
          // Avoid double divider between adjacent accordions
          // when disableGutters is true
          borderBottomWidth: 0,
        },
      }),
      ...(disableGutters && {
        [`& + &`]: {
          // Avoid double divider between adjacent accordions
          // when disableGutters is true
          borderTopWidth: 0,
        },
      }),
    }),
    //
    // Switch alignment
    //
    ...alignmentStyles,
    ...(variant === 'elevation' && {
      ...(!disableGutters && {
        '&:before': {
          // Non-outlined accordions with a gutter between adjacent
          // accordions should never display a divider
          opacity: 0,
        },
      }),
      ...(disableGutters && {
        [`&.${accordionClasses.expanded}:not(:first-of-type)`]: {
          // Non-outlined accordions without a gutter between them
          // should have a divider between adjacent accordions even
          // when it is expanded (divergence from MUI styles)
          '&:before': {
            display: 'block !important',
            opacity: '1 !important',
          },
        },
      }),
    }),
  };
});

export default Accordion;
